import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import {Trash, Loader2, AlertTriangle, ShelvingUnit, Files, File, Plus} from "lucide-react";
import { GetRagCorpora, CreateRagCorpus, DeleteRagCorpus, createCorpusAndRegister } from "../../api/APIGemini";
import { GetCorpusCodes, PostCodes, DeleteCode } from "../../api/APISurvey";
import { GetBots, GetGeminiProject, GetGeminiLocation, updateBotById } from "../../api/APIBot";
import { Modal, Popup } from "../../UIElements";
import UsedByBots from "../../components/UsedByBots/UsedByBots";
import { useSnackbar } from "../../Content";
import { queryClient } from "../../App";
import "./BotCompetences.scss";

const CORPUS_LIMIT = 40;

export default function BotCompetences() {
    const intl = useIntl();
    const history = useHistory();
    const { setSnackbarData } = useSnackbar();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [creatingKB, setCreatingKB] = useState(null); // { title, startTime, status: 'creating' | 'error' }
    const [, setTick] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null); // { code, title, resourceName }
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        document.title = intl.formatMessage({
            id: "botCompetences",
            defaultMessage: "Bot Competences",
        }) + " | Annie Admin"
    }, [intl])

    // Tick every second while creating to update elapsed time
    useEffect(() => {
        if (creatingKB?.status !== 'creating') return;
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, [creatingKB]);

    const corpusCodesQuery = GetCorpusCodes();
    const ragCorporaQuery = GetRagCorpora();
    const botsQuery = GetBots();
    const geminiProjectQuery = GetGeminiProject();
    const geminiLocationQuery = GetGeminiLocation();
    const createRagCorpusMutation = CreateRagCorpus();
    const deleteRagCorpusMutation = DeleteRagCorpus();
    const postCodesMutation = PostCodes();
    const deleteCodeMutation = DeleteCode();

    const corpusCodesMap = corpusCodesQuery.data?.[0] || {};
    const gcpCorpora = ragCorporaQuery.data?.ragCorpora || [];

    // Build unified list from codes table (source of truth for titles)
    const allCorpusList = Object.entries(corpusCodesMap).map(([code, value]) => {
        // The /corpus codes endpoint doesn't return the row-level `updated`
        // column, so derive "last activity" from creation and file uploads.
        const fileUploadTimes = Object.values(value?.files || {})
            .map(f => f?.uploadedAt)
            .filter(Boolean);
        const lastActivity = [value?.createdAt, ...fileUploadTimes]
            .filter(Boolean)
            .sort()
            .pop() || '';
        return {
            code,
            title: value?.title || code,
            resourceName: value?.resourceName,
            createdAt: value?.createdAt,
            lastActivity,
            // Verify it exists in GCP
            existsInGcp: gcpCorpora.some(c => c.name === value?.resourceName)
        };
    }).sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));

    // Build map of corpusId -> bot objects that link each Knowledge Base
    const allBots = botsQuery.data || [];
    const botsForCorpus = {};
    allBots.forEach(bot => {
        Object.values(bot?.config?.corpora || {}).forEach(corpusId => {
            if (!botsForCorpus[corpusId]) botsForCorpus[corpusId] = [];
            botsForCorpus[corpusId].push(bot);
        });
    });

    const corpusList = searchValue.trim()
        ? allCorpusList.filter(c => {
            const q = searchValue.toLowerCase();
            if (c.title.toLowerCase().includes(q)) return true;
            return (botsForCorpus[c.code] || []).some(bot => (bot.config?.name || '').toLowerCase().includes(q));
        })
        : allCorpusList;

    const corpusCount = allCorpusList.length;

    const handleCreate = async () => {
        const title = newTitle.trim();
        if (!title || creatingKB?.status === 'creating') return;

        // Close the modal right away and show a disabled placeholder row in the
        // list while GCP provisions the corpus (~60s). Keeps the user unblocked.
        setNewTitle('');
        setShowCreateModal(false);
        setCreatingKB({ title, startTime: Date.now(), status: 'creating' });

        try {
            const project = geminiProjectQuery?.data[0]?.value;
            const location = geminiLocationQuery?.data[0]?.value;
            const corpusBody = {
                displayName: title,
            };
            if (project && location) {
                corpusBody.vectorDbConfig = {
                    ragEmbeddingModelConfig: {
                        vertexPredictionEndpoint: {
                            endpoint: `projects/${project}/locations/${location}/publishers/google/models/text-multilingual-embedding-002`
                        }
                    }
                };
            }
            await createCorpusAndRegister({
                title,
                corpusBody,
                createMutation: createRagCorpusMutation,
                postCodesMutation,
            });

            queryClient.invalidateQueries('corpusCodes');
            queryClient.invalidateQueries('ragCorpora');

            // The real row takes over from the placeholder once the list refreshes.
            setCreatingKB(null);
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.createSuccess', defaultMessage: 'Knowledge base created successfully' }), state: 'success', autoHide: true });
        } catch (err) {
            console.error('Error creating Knowledge Base:', err);
            setCreatingKB(prev => (prev ? { ...prev, status: 'error' } : null));
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.createError', defaultMessage: 'Failed to create knowledge base. Please try again.' }), state: 'error', autoHide: true });
            // Clear the failed placeholder after a few seconds.
            setTimeout(() => setCreatingKB(null), 5000);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            // Delete from GCP
            if (deleteTarget.code) {
                try {
                    await deleteRagCorpusMutation.mutateAsync(`ragCorpora/${deleteTarget.code}`);
                } catch (err) {
                    // 404 = already deleted on GCP, proceed with codes cleanup
                    if (err.response?.status !== 404) throw err;
                }
            }

            // Delete from codes table
            try {
                await deleteCodeMutation.mutateAsync({ codeset: 'corpus', code: deleteTarget.code });
            } catch (err) {
                // Fallback: post empty value to effectively remove
                await postCodesMutation.mutateAsync([{ codeset: 'corpus', code: deleteTarget.code, value: null }]);
            }

            // Remove KB reference from all linked bot modules
            const linkedBots = allBots.filter(bot =>
                Object.values(bot?.config?.corpora || {}).includes(deleteTarget.code)
            );
            for (const bot of linkedBots) {
                const updatedCorpora = { ...bot.config.corpora };
                for (const [slug, cid] of Object.entries(updatedCorpora)) {
                    if (cid === deleteTarget.code) delete updatedCorpora[slug];
                }
                try {
                    await updateBotById(bot.id, { ...bot, config: { ...bot.config, corpora: updatedCorpora } });
                } catch (err) {
                    console.error(`Failed to unlink KB from bot ${bot.id}:`, err);
                }
            }

            queryClient.invalidateQueries('corpusCodes');
            queryClient.invalidateQueries('ragCorpora');
            queryClient.invalidateQueries('bots');
            setDeleteTarget(null);
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.deleteSuccess', defaultMessage: 'Knowledge base deleted.' }), state: 'success', autoHide: true });
        } catch (err) {
            console.error('Error deleting Knowledge Base:', err);
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.deleteError', defaultMessage: 'Failed to delete knowledge base. Please try again.' }), state: 'error', autoHide: true });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCreateNewCorpusButtonClick = () => {
        if (corpusCount === CORPUS_LIMIT) {
            setSnackbarData({ show: true, text: intl.formatMessage({ id: 'knowledge.limitReached', defaultMessage: 'Knowledge base limit reached. Delete a knowledge base to create a new one.' }), state: 'error', autoHide: true });
        } else {
            setShowCreateModal(true)
        }
    }

    const isLoading = corpusCodesQuery.isLoading || ragCorporaQuery.isLoading;

    return (
        <div className="content bot-competences">
            <div className="header-container">
                <h1>
                    <ShelvingUnit size={24} strokeWidth={2} />
                    {intl.formatMessage({ id: 'botCompetences', defaultMessage: 'Bot Competences' })}
                </h1>
            </div>

            {/* Section tabs */}
            <div className="competences-tab">
                <nav>
                    <ul>
                        <li>
                            <button className="active">
                                <Files size={16} />
                                {intl.formatMessage({ id: 'knowledge', defaultMessage: 'Knowledge' })}
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>

            <div className={"corpus-toolbar toolbar"}>
                <input
                    type="search"
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    placeholder={intl.formatMessage({ id: 'searchKnowledgeBases', defaultMessage: 'Search knowledge bases' })}
                />
                <button className="create-new button" onClick={handleCreateNewCorpusButtonClick} disabled={creatingKB?.status === 'creating'}>
                    <Plus size={16} strokeWidth={3} />
                    {intl.formatMessage({ id: 'knowledge.new', defaultMessage: 'New Knowledge Base' })}
                </button>
                <div className={`corpus-count-bar`}>
                    <span className="corpus-count-badge">{corpusCount} / {CORPUS_LIMIT}</span>
                    {intl.formatMessage({ id: 'knowledge.basesCount', defaultMessage: 'Knowledge bases' })}
                </div>
            </div>


            {/* List */}
            {isLoading ? (
                <div className="loading-state">
                    <Loader2 size={24} className="spinner" />
                </div>
            ) : (corpusList.length === 0 && !creatingKB) ? (
                <div className="empty-state">
                    <ShelvingUnit size={48} />
                    <p>{intl.formatMessage({ id: 'knowledge.empty', defaultMessage: 'No knowledge bases yet. Create one to upload material.' })}</p>
                </div>
            ) : (
                <div className="corpus-list">
                    {creatingKB && (
                        <div className={`corpus-item creating-item ${creatingKB.status}`}>
                            <div className="corpus-item-info">
                                <div className="corpus-item-title">{creatingKB.title}</div>
                                <div className="corpus-item-meta">
                                    {creatingKB.status === 'error' ? (
                                        <>
                                            <AlertTriangle size={12} />
                                            <span>{intl.formatMessage({ id: 'knowledge.createError', defaultMessage: 'Failed to create knowledge base. Please try again.' })}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Loader2 size={12} className="spinner" />
                                            <span>
                                                {intl.formatMessage({ id: 'knowledge.settingUp', defaultMessage: 'Setting up knowledge base...' })}
                                                {` (${Math.floor((Date.now() - creatingKB.startTime) / 1000)}s)`}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {corpusList.map(corpus => {
                        const corpusBots = botsForCorpus[corpus.code] || [];
                        const fileCount = Object.keys(corpusCodesMap[corpus.code]?.files || {}).length;
                        return (
                            <div
                                key={corpus.code}
                                className="corpus-item"
                                onClick={() => history.push(`/bot-competences/knowledge/${corpus.code}`)}
                            >
                                <div className="corpus-item-info">
                                    <div className="corpus-item-title">{corpus.title}</div>
                                    <div className="corpus-item-meta">
                                        <span className="corpus-item-files">
                                            <File size={12} />
                                            {fileCount}
                                        </span>
                                        <span className="corpus-item-meta-separator">·</span>
                                        {corpusBots.length > 0
                                            ? <UsedByBots bots={corpusBots} compact />
                                            : intl.formatMessage({ id: 'knowledge.notLinked', defaultMessage: 'Not linked to any module' })
                                        }
                                    </div>
                                </div>
                                <div className="corpus-item-actions" onClick={e => e.stopPropagation()}>
                                    <button
                                        className="delete-btn"
                                        onClick={() => setDeleteTarget(corpus)}
                                        title={intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })}
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showCreateModal && (
                <Popup closePopup={() => { setShowCreateModal(false); setNewTitle(''); }}>
                    <h3>{intl.formatMessage({ id: 'knowledge.new', defaultMessage: 'New Knowledge Base' })}</h3>
                    <div className="create-kb-form">
                        <label>{intl.formatMessage({ id: 'knowledge.titleLabel', defaultMessage: 'Knowledge base title' })}</label>
                        <input
                            type="text"
                            className="create-kb-input"
                            placeholder={intl.formatMessage({ id: 'knowledge.titlePlaceholder', defaultMessage: 'e.g. HR Policies' })}
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
                            autoFocus
                        />
                    </div>
                    <div className="modal-options">
                        <button className="cancel" onClick={() => { setShowCreateModal(false); setNewTitle(''); }}>
                            {intl.formatMessage({ id: 'modal.cancel', defaultMessage: 'Cancel' })}
                        </button>
                        <button
                            className="confirm"
                            onClick={handleCreate}
                            disabled={!newTitle.trim()}
                        >
                            {intl.formatMessage({ id: 'create', defaultMessage: 'Create' })}
                        </button>
                    </div>
                </Popup>
            )}

            {/* Delete confirmation */}
            {deleteTarget && (
                <Modal
                    header={intl.formatMessage({ id: 'knowledge.deleteTitle', defaultMessage: 'Are you sure you want to delete this knowledge base?' })}
                    text={
                        (botsForCorpus[deleteTarget.code] || []).length > 0
                            ? intl.formatMessage(
                                { id: 'knowledge.deleteWarning', defaultMessage: 'This knowledge base is linked to {count} bot module(s). Deleting it will remove it from those modules and delete all files related to it. This cannot be undone.' },
                                { count: (botsForCorpus[deleteTarget.code] || []).length }
                            )
                            : intl.formatMessage({ id: 'knowledge.deleteConfirm', defaultMessage: 'Deleting it will permanently remove it and all files related to it. This cannot be undone.' })
                    }
                    closeModal={() => setDeleteTarget(null)}
                    confirmAction={handleDelete}
                    confirmDisabled={isDeleting}
                    confirmText={isDeleting
                        ? <><Loader2 size={14} className="spinner" /> {intl.formatMessage({ id: 'deleting', defaultMessage: 'Deleting...' })}</>
                        : intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })}
                    alert={true}
                />
            )}
        </div>
    );
}
